import React, { useContext, useEffect, useRef, useState } from "react";
import { useTheme } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import {
  makeStyles,
  Paper,
  InputBase,
  IconButton,
  Button,
  List,
  Collapse,
  Typography,
  TextField,
  Tooltip,
  Chip,
} from "@material-ui/core";
import {
  ExpandLess,
  ExpandMore,
  Search as SearchIcon,
  Add as AddIcon,
  TextRotateUp,
  TextRotationDown,
  Forum as ForumIcon,
  Close as CloseIcon,
} from "@material-ui/icons";
import { FilterAltOff, FilterAlt, PlaylistAddCheckOutlined } from "@mui/icons-material";

import NewTicketModal from "../NewTicketModal";
import TicketListItem from "../TicketListItemCustom";
import TicketsList from "../TicketsListCustom";
import TabPanel from "../TabPanel";
import { TagsFilter } from "../TagsFilter";
import { MotivoFilter } from "../MotivoFilter";
import { UsersFilter } from "../UsersFilter";
import { StatusFilter } from "../StatusFilter";
import { WhatsappsFilter } from "../WhatsappsFilter";
import { Snackbar } from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { QueueSelectedContext } from "../../context/QueuesSelected/QueuesSelectedContext";

import api from "../../services/api";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { parseSmartSearch } from "../../utils/parseSmartSearch";

const useStyles = makeStyles((theme) => ({
  ticketsWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    boxShadow: theme.mode === "light"
      ? "0 4px 16px rgba(30, 20, 60, 0.10)"
      : "0 4px 16px rgba(0, 0, 0, 0.35)",
  },

  ticketOptionsBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: theme.palette.optionsBackground,
    borderRadius: theme.shape.borderRadius,
    border: "none",
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    padding: theme.spacing(0.5),
    gap: theme.spacing(0.5),
  },

  toolbarBtn: {
    textTransform: "none",
    minWidth: "auto",
    fontSize: "0.72rem",
    padding: theme.spacing(0.4, 0.8),
    color: theme.mode === "light" ? "#445" : "#ddd",
  },

  serachInputWrapper: {
    flex: 1,
    height: 42,
    background: theme.palette.optionsBackground,
    display: "flex",
    borderRadius: 40,
    padding: 4,
    border: "none",
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
    transition: "box-shadow .12s ease-in-out",
    "&:focus-within": {
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}40`,
    },
  },

  searchIcon: {
    color: "grey",
    marginLeft: 6,
    marginRight: 6,
    alignSelf: "center",
  },

  searchInput: {
    flex: 1,
    border: "none",
    borderRadius: 30,
  },

  filterPanel: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    margin: theme.spacing(0, 1, 1, 1),
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 8,
  },

  messagesChip: {
    marginRight: 2,
    flexShrink: 0,
  },

  smartChipsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    padding: theme.spacing(0, 1.5, 0.5, 1.5),
  },

  filterIcon: {
    marginRight: 6,
    alignSelf: "center",
    color: theme.mode === "light" ? theme.palette.primary.main : "#FFF",
    cursor: "pointer",
  },

  icon: {
    color: "#aaa",
    "&:hover": {
      color: theme.mode === "light" ? theme.palette.primary.main : "#FFF",
    },
  },

  snackbar: {
    display: "flex",
    justifyContent: "space-between",
    backgroundColor: theme.palette.primary.main,
    color: "white",
    borderRadius: 30,
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8em",
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "1em",
    },
  },

  yesButton: {
    backgroundColor: "#FFF",
    color: "rgba(0, 100, 0, 1)",
    padding: "4px 4px",
    fontSize: "1em",
    fontWeight: "bold",
    textTransform: "uppercase",
    marginRight: theme.spacing(1),
    "&:hover": {
      backgroundColor: "darkGreen",
      color: "#FFF",
    },
    borderRadius: 30,
  },
  noButton: {
    backgroundColor: "#FFF",
    color: "rgba(139, 0, 0, 1)",
    padding: "4px 4px",
    fontSize: "1em",
    fontWeight: "bold",
    textTransform: "uppercase",
    "&:hover": {
      backgroundColor: "darkRed",
      color: "#FFF",
    },
    borderRadius: 30,
  },

  // Painel agrupado (substitui as guias antigas): secoes fixas -
  // Interagindo com o Bot / Fila de Espera (por setor) / Em Atendimento /
  // Encerrados (com filtro de periodo) - todas visiveis ao mesmo tempo,
  // cada uma expansivel/recolhivel, igual o modelo que o usuario mostrou
  // como referencia.
  sectionsScroll: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },

  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0.7, 1.5),
    cursor: "pointer",
    userSelect: "none",
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
  },

  sectionTitle: {
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: "uppercase",
  },

  sectionList: {
    maxHeight: 320,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },

  sectionEmpty: {
    padding: theme.spacing(1, 2),
    fontSize: "0.78rem",
    color: theme.mode === "light" ? "#888" : "#aaa",
  },

  queueSubHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0.5, 2),
    fontSize: "0.72rem",
    fontWeight: 700,
    color: theme.mode === "light" ? "#555" : "#ccc",
    cursor: "pointer",
    userSelect: "none",
  },

  positionRow: {
    display: "flex",
    alignItems: "center",
  },

  positionBadge: {
    flexShrink: 0,
    width: 22,
    height: 22,
    borderRadius: "50%",
    backgroundColor: theme.mode === "light" ? "#eef1f5" : "#333",
    color: theme.mode === "light" ? "#445" : "#ddd",
    fontSize: "0.68rem",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: theme.spacing(1),
  },

  dateFilterRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
  },

  dateField: {
    "& input": {
      fontSize: "0.78rem",
      padding: "6px 8px",
    },
  },
}));

const TicketsManagerTabs = () => {
  const theme = useTheme();
  const classes = useStyles();
  const history = useHistory();

  const [searchParam, setSearchParam] = useState("");
  const [tab, setTab] = useState("open");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [sortTickets, setSortTickets] = useState(false);

  const [botOpen, setBotOpen] = useState(true);
  const [filaOpen, setFilaOpen] = useState(true);
  const [atendimentoOpen, setAtendimentoOpen] = useState(true);
  // Recolher setor individual dentro de Fila de Espera / Em Atendimento -
  // guarda por queueId, um objeto separado pra cada secao (recolher
  // "Suporte" na fila de espera nao deve recolher "Suporte" em
  // atendimento tambem). true = recolhido.
  const [collapsedFilaQueues, setCollapsedFilaQueues] = useState({});
  const [collapsedAtendimentoQueues, setCollapsedAtendimentoQueues] = useState({});
  const toggleFilaQueue = (queueKey) =>
    setCollapsedFilaQueues((prev) => ({ ...prev, [queueKey]: !prev[queueKey] }));
  const toggleAtendimentoQueue = (queueKey) =>
    setCollapsedAtendimentoQueues((prev) => ({ ...prev, [queueKey]: !prev[queueKey] }));
  // Recolhido por padrao - a lista de encerrados so e' buscada/montada
  // quando o usuario realmente clica pra abrir (ver a montagem
  // condicional do TicketsList abaixo), pra tela abrir mais rapido.
  const [encerradosOpen, setEncerradosOpen] = useState(false);
  const [encerradosEverOpened, setEncerradosEverOpened] = useState(false);

  const todayStr = format(new Date(), "yyyy-MM-dd");
  const [dateStartInput, setDateStartInput] = useState("");
  const [dateEndInput, setDateEndInput] = useState(todayStr);
  const [appliedDateStart, setAppliedDateStart] = useState("");
  const [appliedDateEnd, setAppliedDateEnd] = useState("");

  const searchInputRef = useRef();
  const [searchOnMessages, setSearchOnMessages] = useState(false);

  // Busca inteligente: caixa unica de texto livre que interpreta atalhos
  // de data/status/tag/whatsapp em vez de exigir que o usuario abra os
  // paineis de filtro separados. "allTags"/"allWhatsapps" sao o
  // dicionario que o parser usa pra reconhecer o que foi digitado -
  // carregados uma vez, mesmas listas que TagsFilter/WhatsappsFilter ja
  // buscam internamente.
  const [smartInputValue, setSmartInputValue] = useState("");
  const [smartChips, setSmartChips] = useState([]);
  const [smartDateStart, setSmartDateStart] = useState("");
  const [smartDateEnd, setSmartDateEnd] = useState("");
  const [allTags, setAllTags] = useState([]);
  const [allWhatsapps, setAllWhatsapps] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [tagsRes, whatsappsRes] = await Promise.all([
          api.get("/tags/list"),
          api.get("/whatsapp"),
        ]);
        setAllTags(tagsRes.data || []);
        setAllWhatsapps((whatsappsRes.data || []).map((w) => ({ id: w.id, name: w.name })));
      } catch (err) {
        // Falha aqui so' significa que a busca inteligente nao vai
        // reconhecer tag/whatsapp por nome - a busca por texto simples
        // continua funcionando normalmente.
      }
    })();
  }, []);

  const { user } = useContext(AuthContext);
  // Admin/allUserChat sempre ve tudo agora - a arvore de setores ja da
  // essa visibilidade, nao precisa mais do botao "Todos" pra alternar.
  // Usuario comum continua restrito as proprias filas (ate a permissao
  // por fila/visao ser construida - ver backlog).
  const showAllTickets = user.profile === "admin" || user.allUserChat === "enabled";
  const { profile } = user;
  const { setSelectedQueuesMessage } = useContext(QueueSelectedContext);
  const { setTabOpen } = useContext(TicketsContext);

  const [openCount, setOpenCount] = useState(null);
  const [closedCount, setClosedCount] = useState(null);

  const userQueueIds = user.queues.map((q) => q.id);
  const [selectedQueueIds] = useState(userQueueIds || []);
  // Fila reconhecida na busca inteligente (ex: "comercial") - separado
  // do selectedQueueIds da arvore/setores, pra nao estreitar as secoes
  // da tela normal so' porque o usuario esta pesquisando. Comeca com
  // todas as filas do usuario (igual selectedQueueIds) e so' estreita
  // quando o parser reconhece um nome de fila especifico no texto.
  const [smartQueueIds, setSmartQueueIds] = useState(userQueueIds || []);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedMotivos, setSelectedMotivos] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState([]);
  const [forceSearch, setForceSearch] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [filter, setFilter] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [isFilterActive, setIsFilterActive] = useState(false);

  useEffect(() => {
    setSelectedQueuesMessage(selectedQueueIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedQueueIds]);

  useEffect(() => {
    if (tab === "search") {
      searchInputRef.current.focus();
    }
    setForceSearch(!forceSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  let searchTimeout;

  // Caixa unica de busca: interpreta o texto (data/status/tag/whatsapp
  // reconhecidos viram filtro aplicado automaticamente, o resto vira
  // busca por nome/conteudo) em vez do usuario precisar abrir os paineis
  // manuais. Os paineis continuam existindo, atras do icone de filtro,
  // pra casos que o texto livre nao cobre bem (ex: atendente especifico).
  const handleSmartSearch = (e) => {
    const rawValue = e.target.value;
    setSmartInputValue(rawValue);

    clearTimeout(searchTimeout);

    if (rawValue.trim() === "") {
      setSearchParam("");
      setSmartChips([]);
      setSelectedTags([]);
      setSelectedWhatsapp([]);
      setSelectedStatus([]);
      setSmartDateStart("");
      setSmartDateEnd("");
      setSmartQueueIds(userQueueIds || []);
      setForceSearch(!forceSearch);
      setFilter(false);
      setTab("open");
      return;
    } else if (tab !== "search") {
      setTab("search");
    }

    searchTimeout = setTimeout(() => {
      const parsed = parseSmartSearch(rawValue, { tags: allTags, whatsapps: allWhatsapps, queues: user.queues });
      setSearchParam(parsed.searchParam);
      setSmartChips(parsed.chips);
      setSelectedTags(parsed.tagIds);
      setSelectedWhatsapp(parsed.whatsappIds);
      setSelectedStatus(parsed.statusFilter);
      setSmartDateStart(parsed.dateStart || "");
      setSmartDateEnd(parsed.dateEnd || "");
      setSmartQueueIds(parsed.queueIds.length > 0 ? parsed.queueIds : (userQueueIds || []));
      setForceSearch(!forceSearch);
    }, 500);
  };

  // Remove um chip reconhecido individualmente (o parser interpretou
  // errado) sem precisar apagar a busca inteira e digitar de novo.
  const handleRemoveSmartChip = (chipToRemove) => {
    setSmartChips((prev) => prev.filter((c) => c !== chipToRemove));
    if (chipToRemove.type === "date") {
      setSmartDateStart("");
      setSmartDateEnd("");
    } else if (chipToRemove.type === "status") {
      setSelectedStatus((prev) => prev.filter((s) => s !== chipToRemove.value));
    } else if (chipToRemove.type === "tag") {
      setSelectedTags((prev) => prev.filter((id) => id !== chipToRemove.value));
    } else if (chipToRemove.type === "whatsapp") {
      setSelectedWhatsapp((prev) => prev.filter((id) => id !== chipToRemove.value));
    } else if (chipToRemove.type === "queue") {
      setSmartQueueIds((prev) => {
        const next = prev.filter((id) => id !== chipToRemove.value);
        return next.length > 0 ? next : (userQueueIds || []);
      });
    }
    setForceSearch((prev) => !prev);
  };

  // Saida explicita do modo busca - clicar num "x" em vez de precisar
  // apagar o texto manualmente ou dar F5 (era o unico jeito antes do
  // fix no handleFilter acima).
  const handleClearSmartSearch = () => {
    setSmartInputValue("");
    setSearchParam("");
    setSmartChips([]);
    setSelectedTags([]);
    setSelectedWhatsapp([]);
    setSelectedStatus([]);
    setSmartDateStart("");
    setSmartDateEnd("");
    setSmartQueueIds(userQueueIds || []);
    setForceSearch((prev) => !prev);
    setFilter(false);
    setIsFilterActive(false);
    setTab("open");
  };

  const handleSnackbarOpen = () => setSnackbarOpen(true);
  const handleSnackbarClose = () => setSnackbarOpen(false);

  const CloseAllTicket = async () => {
    try {
      await api.post("/tickets/closeAll", {
        status: "pending",
        selectedQueueIds,
      });
      handleSnackbarClose();
    } catch (err) {
      console.log("Error: ", err);
    }
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const handleSelectedTags = (selecteds) => {
    const tags = selecteds.map((t) => t.id);
    clearTimeout(searchTimeout);
    if (tags.length === 0) {
      setForceSearch(!forceSearch);
    } else if (tab !== "search") {
      setTab("search");
    }
    searchTimeout = setTimeout(() => {
      setSelectedTags(tags);
      setForceSearch(!forceSearch);
    }, 500);
  };

  const handleSelectedMotivos = (selecteds) => {
    const motivos = selecteds.map((t) => t.id);
    clearTimeout(searchTimeout);
    if (motivos.length === 0) {
      setForceSearch(!forceSearch);
    } else if (tab !== "search") {
      setTab("search");
    }
    searchTimeout = setTimeout(() => {
      setSelectedMotivos(motivos);
      setForceSearch(!forceSearch);
    }, 500);
  };

  const handleSelectedUsers = (selecteds) => {
    const users = selecteds.map((t) => t.id);
    clearTimeout(searchTimeout);
    if (users.length === 0) {
      setForceSearch(!forceSearch);
    } else if (tab !== "search") {
      setTab("search");
    }
    searchTimeout = setTimeout(() => {
      setSelectedUsers(users);
      setForceSearch(!forceSearch);
    }, 500);
  };

  const handleSelectedWhatsapps = (selecteds) => {
    const whatsapp = selecteds.map((t) => t.id);
    clearTimeout(searchTimeout);
    if (whatsapp.length === 0) {
      setForceSearch(!forceSearch);
    } else if (tab !== "search") {
      setTab("search");
    }
    searchTimeout = setTimeout(() => {
      setSelectedWhatsapp(whatsapp);
      setForceSearch(!forceSearch);
    }, 500);
  };

  const handleSelectedStatus = (selecteds) => {
    const statusFilter = selecteds.map((t) => t.status);
    clearTimeout(searchTimeout);
    if (statusFilter.length === 0) {
      setForceSearch(!forceSearch);
    } else if (tab !== "search") {
      setTab("search");
    }
    searchTimeout = setTimeout(() => {
      setSelectedStatus(statusFilter);
      setForceSearch(!forceSearch);
    }, 500);
  };

  // Bug corrigido 2026-08-17: antes o "setTab('search')" rodava sempre,
  // mesmo fechando o painel - a tela ficava presa em modo busca pra
  // sempre, so' voltava com F5. Fechar o painel so' volta pra arvore se
  // nao tiver busca por texto ativa tambem (senao perderia o resultado
  // da busca so' por ter fechado o painel avancado).
  const handleFilter = () => {
    if (filter) {
      setFilter(false);
      if (!smartInputValue.trim()) {
        setTab("open");
      }
    } else {
      setFilter(true);
      setTab("search");
    }
  };

  const handleApplyDateFilter = () => {
    setAppliedDateStart(dateStartInput);
    setAppliedDateEnd(dateEndInput);
  };

  // Divide a lista de "pending" em quem ainda esta so conversando com a
  // IA (sem fila) e quem ja esta numa fila de setor esperando atendente -
  // e agrupa esse segundo grupo por setor, com a posicao de cada um na
  // fila (a ordem que a API ja devolve, filtrada por setor).
  const renderPendingSections = (ticketsList, setTabOpenFn) => {
    const botTickets = ticketsList.filter((t) => !t.queueId);
    const queuedTickets = ticketsList.filter((t) => t.queueId);

    const grouped = [];
    queuedTickets.forEach((t) => {
      let group = grouped.find((g) => g.queueId === t.queueId);
      if (!group) {
        group = { queueId: t.queueId, queue: t.queue, tickets: [] };
        grouped.push(group);
      }
      group.tickets.push(t);
    });

    return (
      <>
        <div className={classes.sectionHeader} onClick={() => setBotOpen((p) => !p)}>
          <Typography className={classes.sectionTitle}>
            Interagindo com o bot ({botTickets.length})
          </Typography>
          {botOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </div>
        <Collapse in={botOpen}>
          {botTickets.length === 0 ? (
            <div className={classes.sectionEmpty}>Nenhum contato interagindo com o bot.</div>
          ) : (
            <List disablePadding>
              {botTickets.map((t) => (
                <TicketListItem ticket={t} key={t.id} setTabOpen={setTabOpenFn} />
              ))}
            </List>
          )}
        </Collapse>

        <div className={classes.sectionHeader} onClick={() => setFilaOpen((p) => !p)}>
          <Typography className={classes.sectionTitle}>
            Fila de espera ({queuedTickets.length})
          </Typography>
          {filaOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </div>
        <Collapse in={filaOpen}>
          {grouped.length === 0 ? (
            <div className={classes.sectionEmpty}>Fila de espera vazia.</div>
          ) : (
            grouped.map((g) => {
              const isCollapsed = !!collapsedFilaQueues[g.queueId];
              return (
                <div key={g.queueId}>
                  <div
                    className={classes.queueSubHeader}
                    style={{ color: g.queue?.color }}
                    onClick={() => toggleFilaQueue(g.queueId)}
                  >
                    <span>{g.queue?.name} ({g.tickets.length})</span>
                    {isCollapsed ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
                  </div>
                  <Collapse in={!isCollapsed}>
                    <List disablePadding>
                      {g.tickets.map((t, idx) => (
                        <div key={t.id} className={classes.positionRow}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <TicketListItem ticket={t} setTabOpen={setTabOpenFn} />
                          </div>
                          <span className={classes.positionBadge}>{idx + 1}º</span>
                        </div>
                      ))}
                    </List>
                  </Collapse>
                </div>
              );
            })
          )}
        </Collapse>
      </>
    );
  };

  // "Em Atendimento" tambem agrupado por setor (igual a Fila de Espera) -
  // sem isso ficava uma lista unica com o nome da fila so' numa tag
  // pequena dentro de cada card, dificil de separar visualmente.
  const renderOpenSections = (ticketsList, setTabOpenFn) => {
    const grouped = [];
    ticketsList.forEach((t) => {
      const key = t.queueId || "sem-fila";
      let group = grouped.find((g) => g.key === key);
      if (!group) {
        group = { key, queue: t.queue, tickets: [] };
        grouped.push(group);
      }
      group.tickets.push(t);
    });

    return (
      <>
        {grouped.map((g) => {
          const isCollapsed = !!collapsedAtendimentoQueues[g.key];
          return (
            <div key={g.key}>
              <div
                className={classes.queueSubHeader}
                style={{ color: g.queue?.color }}
                onClick={() => toggleAtendimentoQueue(g.key)}
              >
                <span>{g.queue?.name || "Sem fila"} ({g.tickets.length})</span>
                {isCollapsed ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
              </div>
              <Collapse in={!isCollapsed}>
                <List disablePadding>
                  {g.tickets.map((t) => (
                    <TicketListItem ticket={t} key={t.id} setTabOpen={setTabOpenFn} />
                  ))}
                </List>
              </Collapse>
            </div>
          );
        })}
      </>
    );
  };

  return (
    <Paper elevation={0} variant="outlined" className={classes.ticketsWrapper}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket);
        }}
      />
      <div className={classes.serachInputWrapper}>
        <SearchIcon className={classes.searchIcon} />
        <InputBase
          className={classes.searchInput}
          inputRef={searchInputRef}
          placeholder='Busque por nome, "ontem", "comercial fechados"...'
          value={smartInputValue}
          onChange={handleSmartSearch}
        />
        {(smartInputValue || filter || tab === "search") && (
          <Tooltip title="Limpar busca e voltar">
            <IconButton size="small" onClick={handleClearSmartSearch}>
              <CloseIcon fontSize="small" className={classes.icon} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip placement="top" title={searchOnMessages ? "Buscando também no conteúdo das mensagens" : "Buscar também no conteúdo das mensagens (mais lento)"}>
          <IconButton
            size="small"
            onClick={() => setSearchOnMessages((prev) => !prev)}
            className={classes.messagesChip}
            style={{
              color: searchOnMessages ? theme.palette.primary.main : theme.mode === "light" ? "#8a8398" : "#fff",
              backgroundColor: searchOnMessages
                ? (theme.mode === "light" ? "rgba(91,69,224,0.12)" : "rgba(142,124,245,0.18)")
                : "transparent",
            }}
          >
            <ForumIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton
          style={{ backgroundColor: "transparent", boxShadow: "none", border: "none", borderRadius: "50%" }}
          aria-label="filter"
          className={classes.filterIcon}
          onClick={() => {
            setIsFilterActive((prevState) => !prevState);
            handleFilter();
          }}
        >
          {isFilterActive ? (
            <FilterAlt className={classes.icon} />
          ) : (
            <FilterAltOff className={classes.icon} />
          )}
        </IconButton>
      </div>

      {smartChips.length > 0 && (
        <div className={classes.smartChipsRow}>
          {smartChips.map((chip, idx) => (
            <Chip
              key={`${chip.type}-${chip.value}-${idx}`}
              size="small"
              label={chip.label}
              onDelete={() => handleRemoveSmartChip(chip)}
            />
          ))}
        </div>
      )}

      {filter === true && (
        <div className={classes.filterPanel}>
          <TagsFilter onFiltered={handleSelectedTags} />
          <MotivoFilter onFiltered={handleSelectedMotivos} />
          <WhatsappsFilter onFiltered={handleSelectedWhatsapps} />
          <StatusFilter onFiltered={handleSelectedStatus} />
          {profile === "admin" && (
            <UsersFilter onFiltered={handleSelectedUsers} />
          )}
        </div>
      )}

      <Paper square elevation={0} className={classes.ticketOptionsBox}>
        <Snackbar
          open={snackbarOpen}
          onClose={handleSnackbarClose}
          message={i18n.t("tickets.inbox.closedAllTickets")}
          ContentProps={{ className: classes.snackbar }}
          action={
            <>
              <Button className={classes.yesButton} size="small" onClick={CloseAllTicket}>
                {i18n.t("tickets.inbox.yes")}
              </Button>
              <Button className={classes.noButton} size="small" onClick={handleSnackbarClose}>
                {i18n.t("tickets.inbox.no")}
              </Button>
            </>
          }
        />
        <Button
          size="small"
          className={classes.toolbarBtn}
          startIcon={<AddIcon fontSize="small" />}
          onClick={() => setNewTicketModalOpen(true)}
        >
          {i18n.t("tickets.inbox.newTicket")}
        </Button>
        {user.profile === "admin" && (
          <Button
            size="small"
            className={classes.toolbarBtn}
            startIcon={<PlaylistAddCheckOutlined fontSize="small" style={{ color: theme.mode === "light" ? "green" : "#FFF" }} />}
            onClick={handleSnackbarOpen}
          >
            {i18n.t("tickets.inbox.closedAll")}
          </Button>
        )}
        {tab !== "search" && (
          <Tooltip title={!sortTickets ? "Ordem crescente" : "Ordem decrescente"}>
            <IconButton size="small" onClick={() => setSortTickets((prevState) => !prevState)}>
              {!sortTickets ? (
                <TextRotateUp fontSize="small" className={classes.icon} />
              ) : (
                <TextRotationDown fontSize="small" className={classes.icon} />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Paper>

      <TabPanel value={tab} name="open" className={classes.ticketsWrapper}>
        <div className={classes.sectionsScroll}>
          {/* Fix 2026-08-18: o style inline antigo (height:auto + maxHeight
              + overflowY direto no Paper do TicketsList) brigava com o
              flexbox interno do componente (Paper height:100% + filho
              flex:1 maxHeight:100%) - com poucos tickets na fila nao dava
              pra notar, mas com mais gente esperando o calculo de altura
              ficava ambiguo e o layout quebrava (relatado em producao).
              Em Atendimento/Encerrados logo abaixo ja usavam um jeito que
              funciona (envolver com uma div comum maxHeight+overflow, sem
              mexer no style interno do TicketsList) - aplicando o mesmo
              padrao aqui em vez de reinventar. */}
          <div className={classes.sectionList}>
            <TicketsList
              status="pending"
              selectedQueueIds={selectedQueueIds}
              sortTickets={sortTickets ? "ASC" : "DESC"}
              showAll={user.profile === "admin" || user.allUserChat === 'enabled' ? showAllTickets : false}
              setTabOpen={setTabOpen}
              renderList={renderPendingSections}
            />
          </div>

          <div className={classes.sectionHeader} onClick={() => setAtendimentoOpen((p) => !p)}>
            <Typography className={classes.sectionTitle}>
              Em atendimento {openCount === null ? "" : `(${openCount})`}
            </Typography>
            {atendimentoOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </div>
          <Collapse in={atendimentoOpen}>
            <div className={classes.sectionList}>
              <TicketsList
                status="open"
                showAll={showAllTickets}
                sortTickets={sortTickets ? "ASC" : "DESC"}
                selectedQueueIds={selectedQueueIds}
                updateCount={(val) => setOpenCount(val)}
                setTabOpen={setTabOpen}
                renderList={renderOpenSections}
                emptyTitle=" "
                emptyMessage="Nenhum atendimento ativo."
              />
            </div>
          </Collapse>

          <div
            className={classes.sectionHeader}
            onClick={() => {
              setEncerradosOpen((p) => !p);
              setEncerradosEverOpened(true);
            }}
          >
            <Typography className={classes.sectionTitle}>
              Encerrados {encerradosEverOpened && closedCount !== null ? `(${closedCount})` : ""}
            </Typography>
            {encerradosOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </div>
          <Collapse in={encerradosOpen}>
            <div className={classes.dateFilterRow}>
              <TextField
                className={classes.dateField}
                type="date"
                size="small"
                variant="outlined"
                value={dateStartInput}
                onChange={(e) => setDateStartInput(e.target.value)}
              />
              <Typography variant="body2">→</Typography>
              <TextField
                className={classes.dateField}
                type="date"
                size="small"
                variant="outlined"
                value={dateEndInput}
                onChange={(e) => setDateEndInput(e.target.value)}
              />
              <Button size="small" variant="contained" color="primary" onClick={handleApplyDateFilter}>
                Aplicar
              </Button>
            </div>
            <div className={classes.sectionList}>
              {encerradosEverOpened && (
                <TicketsList
                  status="closed"
                  showAll={showAllTickets}
                  selectedQueueIds={selectedQueueIds}
                  setTabOpen={setTabOpen}
                  dateStart={appliedDateStart || undefined}
                  dateEnd={appliedDateEnd || undefined}
                  updateCount={(val) => setClosedCount(val)}
                  emptyTitle=" "
                  emptyMessage="Nenhum atendimento encerrado neste período."
                />
              )}
            </div>
          </Collapse>
        </div>
      </TabPanel>

      <TabPanel value={tab} name="search" className={classes.ticketsWrapper}>
        {profile === "admin" && (
          <TicketsList
            statusFilter={selectedStatus}
            searchParam={searchParam}
            showAll={showAllTickets}
            tags={[...selectedTags, ...selectedMotivos]}
            users={selectedUsers}
            selectedQueueIds={smartQueueIds}
            whatsappIds={selectedWhatsapp}
            forceSearch={forceSearch}
            searchOnMessages={searchOnMessages}
            dateStart={smartDateStart || undefined}
            dateEnd={smartDateEnd || undefined}
            status="search"
          />
        )}

        {profile === "user" && (
          <TicketsList
            statusFilter={selectedStatus}
            searchParam={searchParam}
            showAll={false}
            tags={[...selectedTags, ...selectedMotivos]}
            selectedQueueIds={smartQueueIds}
            whatsappIds={selectedWhatsapp}
            forceSearch={forceSearch}
            searchOnMessages={searchOnMessages}
            dateStart={smartDateStart || undefined}
            dateEnd={smartDateEnd || undefined}
            status="search"
          />
        )}
      </TabPanel>
    </Paper >
  );
};

export default TicketsManagerTabs;
